import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Lock, Envelope, ArrowRight } from "@phosphor-icons/react";
import { VendeIALogo } from "@/components/Brand";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      const dest = loc.state?.from || "/";
      nav(dest, { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2, "No se pudo iniciar sesión"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="w-full max-w-md" data-testid="login-container">
        <div className="mb-10 text-center">
          <div className="overline text-klein">Plataforma</div>
          <div className="mt-2 inline-flex items-center gap-3">
            <VendeIALogo size={56} withText={false} />
            <span className="h-display text-5xl font-black tracking-tighter leading-none">
              Vende<span className="text-klein">IA</span>
            </span>
          </div>
          <p className="text-sm text-muted2 mt-3">
            Ingresa con tu cuenta para gestionar inventario, ventas y reportes.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4 border border-zinc-200 p-8" data-testid="login-form">
          <div>
            <label className="overline block mb-2">Email</label>
            <div className="flex items-center border border-zinc-300 px-3 focus-within:border-klein">
              <Envelope size={16} className="text-muted2" />
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                data-testid="login-email-input"
                data-help="Ingresa el correo con el que se registró tu cuenta."
                className="flex-1 px-3 py-3 text-sm outline-none"
              />
            </div>
          </div>

          <div>
            <label className="overline block mb-2">Contraseña</label>
            <div className="flex items-center border border-zinc-300 px-3 focus-within:border-klein">
              <Lock size={16} className="text-muted2" />
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                data-help="Mínimo 6 caracteres. Si es la primera vez, usa la contraseña que te dio el administrador."
                className="flex-1 px-3 py-3 text-sm outline-none"
              />
            </div>
          </div>

          {err && (
            <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2" data-testid="login-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            data-help="Verifica tu correo y contraseña y abre tu sesión segura."
            className="w-full bg-klein text-white py-3 text-sm font-medium hover:bg-klein/90 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? "Ingresando…" : "Ingresar"}
            {!loading && <ArrowRight size={16} />}
          </button>

          <div className="flex items-center justify-between pt-1">
            <Link
              to="/forgot-password"
              className="text-xs text-klein hover:underline"
              data-testid="forgot-password-link"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
